import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import StatCountUp from "../components/StatCountUp";

const reveal = { initial: { opacity: 0, y: 40 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" }, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } };

const RESULTS = [
  { big: "27,000", label: "MEMBERS ACQUIRED",
    sub: "Scouts Canada", note: "National Youth Recruitment", slug: "ditch-the-screens" },
  { big: "$7.56M", label: "ESTIMATED MEMBERSHIP VALUE",
    sub: "Based on 27,000 memberships", note: "at $280 each", slug: "ditch-the-screens" },
  { big: "$700K", label: "ANNUAL FUNDRAISING REVENUE",
    sub: "Scout Popcorn", note: "Up from approximately $200K", slug: "scout-popcorn" },
  { big: "2,000+", label: "VOLUNTEER LEADS",
    sub: "Scouts Canada", note: "National Volunteer Recruitment", slug: "scouts-volunteer-recruitment" },
  { big: "9×+", label: "FUNDS RAISED VS MEDIA SPEND",
    sub: "Raise Chess Academy · Giving Tuesday",
    note: "$100 CAD media spend → $961 USD raised. Spend and funds are in different currencies — a simple campaign efficiency indicator, not a calculated ROAS.",
    slug: "raise-chess-giving-tuesday" },
  { big: "CHIPMUNKS", label: "EARLY YEARS PROGRAM DEVELOPMENT",
    sub: "Scouts Canada", note: "Naming · Positioning · Testing · Pilot", slug: "chipmunks" },
];

export default function About() {
  return (
    <main data-testid="studio-page" className="bg-black pt-32 pb-8">
      {/* HERO */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 mb-20">
        <motion.p {...reveal} className="overline mb-8">Good Bandit · Studio</motion.p>
        <motion.h1 {...reveal} className="font-display text-6xl md:text-8xl lg:text-9xl text-white leading-[0.92]">
          Creative<br />
          &amp; <span className="italic text-[var(--gb-gold)]">strategy.</span>
        </motion.h1>
        <motion.div {...reveal} className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-16">
          <div className="md:col-span-8 text-white/70 text-base md:text-lg leading-[1.75] space-y-6">
            <p>Good Bandit is an independent creative studio founded by Chesney Gordon-Williams.</p>
            <p>The studio works across campaign strategy, film, photography, digital, paid media and brand development — bringing creative and marketing together under one practice.</p>
            <p>The work ranges from automotive and brand storytelling to national recruitment, fundraising, membership growth and community-focused programs.</p>
          </div>
        </motion.div>
      </section>

      {/* SELECTED RESULTS */}
      <section className="border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <motion.p {...reveal} className="overline mb-3">Selected Results</motion.p>
          <motion.h2 {...reveal} className="font-display text-4xl md:text-5xl text-white leading-[1] mb-16 max-w-3xl">
            Range and scale of the <span className="italic text-[var(--gb-gold)]">work.</span>
          </motion.h2>

          <div className="divide-y divide-white/10">
            {RESULTS.map((r, i) => (
              <motion.div {...reveal} key={r.label + i} className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10 md:py-14" data-testid={`result-${i}`}>
                <div className="md:col-span-6">
                  <p className="font-display text-6xl md:text-8xl text-white leading-[0.9]"><StatCountUp value={r.big} /></p>
                  <p className="kbd text-[var(--gb-gold)] mt-4">{r.label}</p>
                </div>
                <div className="md:col-span-5 md:col-start-8 text-white/60 text-sm leading-relaxed">
                  <p className="text-white/85">{r.sub}</p>
                  <p className="mt-2">{r.note}</p>
                  {r.slug && (
                    <Link to={`/impact/${r.slug}`} className="gb-link inline-flex items-center gap-2 mt-5 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-[var(--gb-gold)]">
                      View case study <ArrowRight size={12} />
                    </Link>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT CHESNEY */}
      <section className="border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
          <motion.div {...reveal} className="md:col-span-5">
            <p className="overline mb-4">About Chesney</p>
            <h3 className="font-display text-4xl md:text-6xl text-white leading-[0.98]">
              Marketer.
              <br />Creative director.
            </h3>
          </motion.div>
          <motion.div {...reveal} className="md:col-span-6 md:col-start-7 text-white/70 leading-[1.75] space-y-5 text-base md:text-lg">
            <p>Chesney Gordon-Williams is a marketer and creative director working across strategy, campaigns and content.</p>
            <p>His experience spans national acquisition campaigns, fundraising, program development, paid media, CRM, film and photography.</p>
            <p>Good Bandit brings those disciplines together into one independent studio.</p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="font-display text-3xl md:text-4xl text-white leading-[1]">
            See the <span className="italic text-[var(--gb-gold)]">work.</span>
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/impact" className="gb-btn-outline text-[10px]">View Impact <ArrowRight size={12} /></Link>
            <Link to="/nocturne" className="gb-btn-outline text-[10px]">Enter Nocturne <ArrowRight size={12} /></Link>
            <Link to="/contact" className="gb-btn-primary">Start a project <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
